/**
 * EVENTSFLO-CLIENT-REQUEST-SHIFT-RULES-AND-TIME-MATHS-FIX-V1
 *
 * Shared shift time-maths utility.
 *
 * Initial consumer: Client Portal / Admin Client Request shift form.
 * Designed for reuse across Project Detail shift modal, All Shifts,
 * Calendar create, Sheet Crew Planner, Public Quote importer, and AI
 * parser review — adopt incrementally to keep diffs small.
 *
 * Contract:
 *   - All times use 24h "HH:MM" string format.
 *   - Decimal hours are supported (e.g. 7.5 → +7h30).
 *   - Overnight shifts are supported: if end <= start, end is treated
 *     as the next day (e.g. 22:00 → 02:00 = 4h).
 *   - Invalid or missing inputs return null instead of throwing.
 *
 * IMPORTANT: this utility is pure (no DOM / no React). Callers decide
 * the policy for which field is authoritative (typically: start +
 * duration → derive end; user edit of end → derive duration).
 */

const TIME_RE = /^(\d{1,2}):(\d{2})$/;

/**
 * Coerce a value into a canonical "HH:MM" 24h string, or null if it
 * cannot be parsed. Tolerates "9:00", "09:00", "9:5", "9:05" inputs.
 */
export function normaliseTimeInput(value) {
  if (value == null || value === "") return null;
  const str = String(value).trim();
  const m = TIME_RE.exec(str);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Convert a normalised "HH:MM" into a total minute count. */
function timeToMinutes(value) {
  const t = normaliseTimeInput(value);
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

/** Convert a total minute count back to "HH:MM" (mod 24h). */
function minutesToTime(total) {
  if (!Number.isFinite(total)) return null;
  const positive = ((Math.round(total) % 1440) + 1440) % 1440;
  const h = Math.floor(positive / 60);
  const m = positive % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Coerce numeric / string duration into a finite number > 0, else null. */
function normaliseDuration(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * Add a duration (in hours, decimal-aware) to a start time and return
 * the resulting "HH:MM". Wraps past midnight (overnight shifts).
 *
 * @param {string} startTime  "HH:MM"
 * @param {number|string} durationHours  e.g. 8 or 7.5
 * @returns {string|null}
 *
 * @example
 *   calculateEndTime("09:00", 8)   // "17:00"
 *   calculateEndTime("09:00", 7.5) // "16:30"
 *   calculateEndTime("22:00", 4)   // "02:00"
 */
export function calculateEndTime(startTime, durationHours) {
  const startMin = timeToMinutes(startTime);
  const dur = normaliseDuration(durationHours);
  if (startMin == null || dur == null) return null;
  const endMin = startMin + dur * 60;
  return minutesToTime(endMin);
}

/**
 * Compute duration in decimal hours between start and end time.
 * If end is on or before start (overnight), wraps to the next day.
 * Returns null for invalid inputs.
 *
 * @param {string} startTime  "HH:MM"
 * @param {string} endTime    "HH:MM"
 * @returns {number|null}
 *
 * @example
 *   calculateDurationHours("09:00", "17:00") // 8
 *   calculateDurationHours("09:00", "16:30") // 7.5
 *   calculateDurationHours("22:00", "02:00") // 4
 */
export function calculateDurationHours(startTime, endTime) {
  const startMin = timeToMinutes(startTime);
  const endMinRaw = timeToMinutes(endTime);
  if (startMin == null || endMinRaw == null) return null;
  let endMin = endMinRaw;
  if (endMin <= startMin) endMin += 1440;
  const diffMinutes = endMin - startMin;
  // Round to nearest minute then convert back to hours so 7.5h
  // doesn't surface 7.499999.
  return Math.round(diffMinutes) / 60;
}
